import { apiClient } from './client';

export interface ConversationRequest {
  message: string;
  conversationId?: string;
  context?: {
    // Page context
    currentPage?: string;
    pageType?: string;
    
    // Celebration context
    celebrationId?: number;
    celebrationTitle?: string;
    celebrationType?: string;
    celebrationDescription?: string;
    
    // Event context
    eventId?: string;
    eventName?: string;
    eventType?: string;
    eventDescription?: string;
    eventDate?: string;
    
    // Legacy fields for backward compatibility
    previousActions?: string[];
  };
}

export interface ConversationResponse {
  success: boolean;
  response: string[];
  action: 'collect_fields' | 'confirm' | 'execute' | 'complete';
  extractedFields?: Record<string, any>;
  missingFields?: string[];
  conversationId: string;
  status: string;
  eventId?: string;
  nextSteps?: string[];
  options?: Array<string | { label: string; value?: string }>;
  mcpTools?: Array<{
    domain: string;
    tools: string[];
  }>;
  intentClassification?: {
    operation: string;
    targetMCP: string;
    toolName: string;
    confidence: number;
    reasoning: string;
  };
}

export const aiService = {
  async processConversation(request: ConversationRequest): Promise<ConversationResponse> {
    const maxRetries = 2;
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // --- Ensure payload matches backend expectations ---
    // Ensure payload includes a valid 'messages' array for MCP backend
    let payload: Record<string, unknown> = { ...request };
    if (request.context) {
      payload = { ...payload, ...request.context };
      delete payload.context;
    }
    // If 'messages' is missing, build it from 'message' field
    if (!('messages' in payload) || !Array.isArray(payload.messages)) {
      if (typeof payload.message === 'string' && payload.message.trim()) {
        payload.messages = [{ role: 'user', content: payload.message }];
      } else {
        throw new Error('No messages provided');
      }
    }
        const response = await apiClient.post('/chat', payload);
        
        // Validate response structure
        if (!response.data || typeof response.data !== 'object') {
          throw new Error('Invalid response format from AI service');
        }

        // Ensure required fields are present in the response
        if (typeof response.data.success !== 'boolean' || !Array.isArray(response.data.response)) {
          throw new Error('AI service response missing required fields: success or response');
        }

        // Build the ConversationResponse object robustly
        const conversationResponse: ConversationResponse = {
          success: response.data.success,
          response: response.data.response,
          action: response.data.action ?? 'collect_fields',
          conversationId: response.data.conversationId ?? '',
          status: response.data.status ?? 'processing',
          extractedFields: response.data.extractedFields ?? {},
          missingFields: response.data.missingFields ?? [],
          nextSteps: response.data.nextSteps ?? [],
          eventId: response.data.eventId,
          options: response.data.options,
          mcpTools: response.data.mcpTools,
          intentClassification: response.data.intentClassification
        };

        return conversationResponse;
      } catch (error: unknown) {
        lastError = error;
        // Don't retry for client errors (4xx) or authentication issues
        if (
          typeof error === 'object' && error !== null &&
          'response' in error &&
          typeof (error as { response?: unknown }).response === 'object' &&
          (error as { response?: unknown }).response !== null &&
          'status' in (error as { response: { status?: number } }).response &&
          typeof (error as { response: { status?: number } }).response.status === 'number' &&
          (error as { response: { status: number } }).response.status >= 400 &&
          (error as { response: { status: number } }).response.status < 500
        ) {
          break;
        }
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }
        // Wait before retrying (exponential backoff)
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // Enhance error with more context before throwing
    if (lastError) {
      lastError.aiServiceContext = {
        maxRetries,
        requestData: request,
        timestamp: new Date().toISOString()
      };
    }
    
    throw lastError;
  },

  async getAvailableTools(eventType?: string): Promise<Array<{domain: string; tools: string[]}>> {
    try {
      const params = eventType ? { eventType } : {};
      const response = await apiClient.get('/chat/tools', { params });
      return response.data.tools || [];
    } catch (error: any) {
      console.error('🔴 AI Service: Error getting tools:', error);
      return [];
    }
  },

  async executeTool(toolName: string, domain: string, parameters: Record<string, any>): Promise<any> {
    try {
      const response = await apiClient.post('/chat/execute-tool', {
        toolName,
        domain,
        parameters
      });
      return response.data;
    } catch (error: any) {
      console.error('🔴 AI Service: Error executing tool:', error);
      throw error;
    }
  },

  async testProvider(provider: string, message: string) {
    const response = await apiClient.post('/mcp/ai/test', { provider, message });
    return response.data;
  },

  async getProviders() {
    const response = await apiClient.get('/mcp/ai/providers');
    return response.data;
  },

  async getCurrentProvider() {
    const response = await apiClient.get('/ai/provider/current');
    return response.data;
  }
};
